/**
 * ConversationSaveDialog — « Créer la conversation » / « Enregistrer la conversation »
 * (UI-SPEC §2.23), opened by the form's button.
 *
 * [B] the quota line from the form's LAST preview `figuresAfter` (the form already refused what
 * does not fit: no over line, no quota dialog). [A] launches and credits per month, then the C17.3
 * lines of the warnings `POST .../conversations/preview` answers when this dialog opens with the
 * exact save body. « Confirmer » is never disabled, but inert while its own request (or the agency
 * warnings preview) is in flight (H3): `createPersona` has no idempotency, a second POST is a
 * second scheduled conversation. Nothing is retried automatically; it never calls `/run` (C17.5).
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AllowanceFigures, Cadence, ConversationLoad, ConversationPersona, SaveWarnings } from '@traaker/shared';
import { cadenceToDays } from '@traaker/shared';
import { conversationCreditsPerMonth, isUnbounded, runsLeft, saveErrorKey, saveErrorOf, singleFlight } from './conversationModel';
import type { ConfirmFigures, SaveError } from './conversationModel';
import { conversationsApi, isAgencyCheck, savedPersona } from './conversationsApi';
import type { SavedPersona } from './conversationsApi';

export interface ConversationSaveDialogProps {
  brandId: string;
  /** null = create. */
  original: ConversationPersona | null;
  isAgency: boolean;
  /** The save body (`personaPayload`). */
  body: Record<string, unknown>;
  figures: ConfirmFigures;
  cadence: Cadence;
  /** Agency « Plafond » (credits per month). */
  maxTurns: number;
  /** [B] the form's last preview (`figuresAfter`). */
  lastPreview: AllowanceFigures | null;
  onClose: () => void;
  onSaved: (persona: ConversationPersona, load: ConversationLoad) => void;
  /** A 409 ALLOWANCE_EXCEEDED anyway (another editor, a new month): back to the form, draft kept. */
  onRefused: (figuresAfter: AllowanceFigures) => void;
  /** A failure with no HTTP status, or a conflict: the form reloads the list. */
  onUncertain: (error: SaveError) => void;
}

/** `JJ/MM` (FR) or `DD/MM` (EN) of an ISO date, in the UI language. */
function dayMonth(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(lang || 'fr', { day: '2-digit', month: '2-digit' }).format(new Date(iso));
}

/** The C17.3 lines of an agency `SaveWarnings[]` (UI-SPEC §2.25 wording); nothing for `[]`. */
export function WarningLines({ warnings }: { warnings: SaveWarnings[] }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      {warnings.map((w, i) => (
        <Box key={i} sx={{ mt: 1 }}>
          <Box sx={{ fontSize: 13.5, color: '#b45309' }}>
            {t('promptStudio.quota.balanceDays', { d: w.agencyBalanceDays })}{' '}
            <Box component="a" href="/credits/ledger" sx={{ color: '#7c3aed' }}>{t('promptStudio.quota.topUp')}</Box>
          </Box>
          {w.appliesFrom && (
            <Box sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
              {t('promptStudio.quota.appliesFrom', { date: dayMonth(w.appliesFrom, i18n.language) })}
            </Box>
          )}
        </Box>
      ))}
    </>
  );
}

/**
 * The dialog frame. While a request runs the backdrop does not close it: a dialog closed and
 * opened again would bring a new « Confirmer », and so a second POST (H3).
 */
export function ConversationSaveDialog(props: ConversationSaveDialogProps) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onClose={busy ? undefined : props.onClose} maxWidth={false}
      PaperProps={{ sx: { width: 440, borderRadius: 1.5, p: 3, boxShadow: '0 1px 2px rgba(0,0,0,.03), 0 24px 70px rgba(0,0,0,.22)' } }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(11,11,15,.3)' } } }}>
      <SaveDialogContent {...props} onBusy={setBusy} />
    </Dialog>
  );
}

/** The dialog's content (exported for the SSR tests: MUI's Dialog renders in a portal). */
export function SaveDialogContent({
  brandId,
  original,
  isAgency,
  body,
  figures,
  cadence,
  maxTurns,
  lastPreview,
  onClose,
  onSaved,
  onRefused,
  onUncertain,
  onBusy = () => {},
}: ConversationSaveDialogProps & { onBusy?: (busy: boolean) => void }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [line, setLine] = useState<string | null>(null);
  // [A] the warnings preview of the exact save body, asked when the dialog opens.
  const [warnings, setWarnings] = useState<{ status: 'pending' | 'done' | 'failed'; list: SaveWarnings[] }>({ status: 'pending', list: [] });

  useEffect(() => {
    if (!isAgency) return;
    let live = true;
    const previewBody = original ? { ...body, personaId: original.personaId } : body;
    conversationsApi.preview(brandId, previewBody).then(
      (c) => { if (live) setWarnings({ status: 'done', list: isAgencyCheck(c) ? c.warnings : [] }); },
      () => { if (live) setWarnings({ status: 'failed', list: [] }); },
    );
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = useRef(singleFlight(async () => {
    setSaving(true);
    setLine(null);
    try {
      const r: SavedPersona = await conversationsApi.save(brandId, original, body);
      onSaved(savedPersona(r), r.load);
    } catch (err) {
      const e = saveErrorOf(err);
      if (e.kind === 'refused') { onRefused(e.figuresAfter); return; }
      if (e.kind === 'network' || e.kind === 'conflict') { onUncertain(e); return; }
      // A structural 400 (a race the form prevents) and the other refusals: the dialog stays open.
      setLine(e.kind === 'invalid' ? e.message : t(saveErrorKey(e) ?? 'promptStudio.common.saveFailed'));
    } finally {
      setSaving(false);
    }
  })).current;
  const inert = saving || (isAgency && warnings.status === 'pending');
  useEffect(() => { onBusy(saving); }, [saving]); // eslint-disable-line react-hooks/exhaustive-deps

  // The equation, its total in bold (UI-SPEC §2.23).
  const equation = t('promptStudio.save.equation', {
    langs: t('promptStudio.save.langs', { count: figures.languages }),
    places: t('promptStudio.save.places', { count: figures.places }),
    engines: t('promptStudio.save.engines', { count: figures.engines }),
    runs: t('promptStudio.save.runs', { count: figures.runs }),
    total: t('promptStudio.save.runs', { count: figures.perLaunch }),
  });
  const [left, right] = equation.split(' = ');

  const monthly = cadence !== 'off' && cadence.unit === 'months';
  const cad = monthly ? t('promptStudio.save.monthShort') : cadence !== 'off' ? t('promptStudio.save.dayShort', { d: cadenceToDays(cadence) }) : '';
  const month = figures.perLaunch * figures.launchesPerMonth;
  let quotaLine: { text: string; amber?: boolean } | null = null;
  if (!isAgency && lastPreview) {
    if (isUnbounded(lastPreview, false)) quotaLine = { text: t('promptStudio.conv.unbounded'), amber: true };
    else {
      const l = runsLeft(lastPreview);
      quotaLine = { text: t(l > 0 ? 'promptStudio.save.selfLeft' : 'promptStudio.save.selfReached', { cad, month, left: l }) };
    }
  }

  return (
    <>
      <Box sx={{ fontSize: 15, fontWeight: 700, mb: 1.5 }}>
        {t(original ? 'promptStudio.save.titleEdit' : 'promptStudio.save.titleCreate')}
      </Box>
      <Box sx={{ fontSize: 13.5, color: 'text.secondary' }}>
        {left}{right !== undefined && <> = <b>{right}</b></>}
      </Box>

      {isAgency ? (
        <>
          <Box sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.5 }}>
            {cadence === 'off'
              ? t('promptStudio.pop.manualTitle')
              : monthly
                ? t('promptStudio.save.agencyMonthly')
                : t('promptStudio.save.agencyLaunches', { d: cadenceToDays(cadence), n: figures.launchesPerMonth })}
          </Box>
          <Box sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
            {t('promptStudio.save.agencyCredits', { n: conversationCreditsPerMonth(figures.perLaunch, maxTurns, cadence) })}
          </Box>
          {warnings.status === 'pending' && <CircularProgress size={14} sx={{ mt: 1 }} />}
          {warnings.status === 'failed' && <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 1 }}>{t('promptStudio.fit.previewFailed')}</Box>}
          <WarningLines warnings={warnings.list} />
        </>
      ) : (
        quotaLine && (
          <Box sx={{ fontSize: 12.5, color: quotaLine.amber ? '#b45309' : 'text.secondary', mt: 0.75 }}>{quotaLine.text}</Box>
        )
      )}

      {line && <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 1 }}>{line}</Box>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
        <Button size="small" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
        <Button size="small" variant="contained" sx={{ fontWeight: 600 }}
          onClick={() => { if (!inert) void confirm(); }}>
          {saving ? <CircularProgress size={14} color="inherit" /> : t('promptStudio.save.confirm')}
        </Button>
      </Box>
    </>
  );
}
